<template>
  <div class="w-100">
    <div class="list-box-upload" :class="{ 'error': validateError }">
      <div>
        <p class="sub-title" v-if="subTitle">{{ subTitle }}</p>
        <el-upload
          :file-list="internalFileList"
          :multiple="multiple"
          :action="action"
          :drag="drag"
          :disabled="disabled"
          :before-upload="beforeUpload"
          :http-request="(res) => httpRequest(res, type)"
          :accept="accept"
          list-type="picture-card"
          class="custom-upload-item"
          @click="handleClick"
          @update:file-list="updateFileList"
          :on-remove="handleInternalRemove"
          :picture-card-size="{ width: '70px', height: '70px' }"
        >
          <template #default>
            <div>
              <font-awesome-icon icon="upload" class="primary" size="lg" />
              <div class="placeholder-text">{{ label }}</div>
            </div>
          </template>
  
          <template #file="{ file }">
            <div class="custom-thumb">
              <img :src="file.url" class="preview-img" :alt="file.name"/>
              <div v-if="isPreview" class="overlay" @click.stop="previewImage(file)">
                <el-icon>
                  <FullScreen />
                </el-icon>
              </div>
            </div>
          </template>
        </el-upload>
        <el-image-viewer 
          v-if="showPreview" 
          :url-list="[previewImageUrl]" 
          @close="closeImageUrl" 
          :teleported="true"
        />
      </div>
      <div class="sub-title">
        {{ description }}
      </div>
    </div>
    <div class="sub-title text-danger my-1" v-if="validateError">
      {{ $t('error.vuelidate.required') }}
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  modelValue: Array,
  multiple: { type: Boolean, default: true },
  action: { type: String, default: '#' },
  drag: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  accept: { type: String, default: '.jpg,.jpeg,.png' },
  label: { type: String, required: true },
  type: { type: String, required: true },
  beforeUpload: Function,
  httpRequest: Function,
  handleRemove: Function,
  handleClick: Function,
  subTitle: { type: String, default: '' },
  isPreview: { type: Boolean, default: true },
  validateError: { type: Boolean, default: false },
  description: { type: String, default: '' }
});

const previewImageUrl = ref('');
const showPreview = ref(false);

const emit = defineEmits(['update:modelValue']);

const internalFileList = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

function updateFileList(newList) {
  internalFileList.value = newList;
}

function handleInternalRemove(file) {
  const updatedList = internalFileList.value.filter((f) => f.uid !== file.uid);
  emit('update:modelValue', updatedList);
  props.handleRemove?.(file, props.type);
}

function previewImage(file) {
  if (!props.isPreview) return;
  previewImageUrl.value = file.url;
  showPreview.value = true;
  emit('open-preview', file.url)
}

function closeImageUrl() {
  showPreview.value = false;
  emit('close-preview')
}
</script>

<style scoped>
.custom-upload-item {
  width: 100%;
}

:deep(.el-upload--picture-card) {
  width: 76px !important;
  height: 76px !important;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 76px !important;
  height: 76px !important;
  margin: 0 !important;
  display: flex;
}

:deep(.el-upload-list--picture-card) {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
}

.placeholder-text {
  font-size: 12px;
  line-height: 16px;
}

.sub-title {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.67);
  margin-bottom: 10px;
}

.custom-thumb {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--el-border-color);
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
  cursor: pointer;
}

.custom-thumb:hover .overlay {
  opacity: 1;
}

.overlay :deep(.el-icon) {
  font-size: 20px;
  color: white;
}

.file-invalid {
  border: 1px solid red !important;
  border-radius: 4px;
}

::v-deep(.el-upload-list--picture-card .el-upload-list__item) {
  border: none !important;
  box-shadow: none !important;
}

::v-deep(.el-upload.is-disabled .el-upload-dragger) {
  background-color: transparent !important;
}

</style>
